import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import useHttp from "../../hooks/use-http";
import { RootState } from "../../store";
import { useAppDispatch } from "../../store/hooks";
import { EventsActions, EventType } from "../../store/events-slice";
import EventItem from "./EventItem";
import classes from "./Events.module.css";

// How long the list holds its order after a star is clicked. Important events
// sort first, so without this the row you just starred jumps away from the
// pointer at the moment you are looking at it (the mobile app does the same).
const SORT_SETTLE_MS = 1000;

const Events = () => {
  const dispatch = useAppDispatch();
  const { uid } = useSelector((state: RootState) => state.auth);
  const { sendRequest } = useHttp();

  const currentDate = new Date();
  const year = currentDate.getFullYear();

  const { windowWidth, windowHeight, addEvent } = useSelector((state: RootState) => state.ui);
  const { userSelectedDate, effectiveToday, events, eventToEdit } = useSelector((state: RootState) => state.events);
  const allEvents = events;

  const wrapperClass = `${classes.wrapper} ${(addEvent || eventToEdit !== null) ? classes.hidden: ''} ${
    window.desktop ? classes.wrapperDesktop : ""
  }`;

  const listClass = `${classes.list} ${
    window.desktop ? classes.listDesktop : ""
  }`;

  const todayDate = effectiveToday;

  // overdue events that haven't been closed yet show up on today's plan,
  // not on the day they were originally scheduled for
  let selectedDateEvents;
  if (userSelectedDate === todayDate) {
    selectedDateEvents = allEvents.filter(
      (event: EventType) =>
        event.date === userSelectedDate ||
        new Date(event.date).getTime() < currentDate.getTime()
    );
  } else {
    selectedDateEvents = allEvents.filter(
      (event: EventType) => event.date === userSelectedDate
    );
  }

  // The order captured when a star was clicked, held for SORT_SETTLE_MS.
  const [frozenOrder, setFrozenOrder] = useState<string[] | null>(null);
  const shownOrder = useRef<string[]>([]);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (settleTimer.current) {
        clearTimeout(settleTimer.current);
      }
    },
    []
  );

  // Starred straight from the list, without opening the event. The star flips
  // at once and is put back if the save fails.
  const toggleImportantHandler = useCallback(
    async ({ onToggleImportant, ...event }: EventType & { onToggleImportant?: unknown }) => {
      setFrozenOrder(shownOrder.current);
      if (settleTimer.current) {
        clearTimeout(settleTimer.current);
      }
      const important = !event.important;
      dispatch(EventsActions.updateEvent({ ...event, important }));
      let saved = false;
      await sendRequest(
        {
          dbPath: "/tasks/" + uid + "/" + event.id,
          method: "PATCH",
          body: { important },
          headers: { "Content-Type": "application/json" },
          hideAfterRequest: true,
        },
        () => {
          saved = true;
        }
      );
      if (!saved) {
        dispatch(EventsActions.updateEvent(event));
      }
      settleTimer.current = setTimeout(() => setFrozenOrder(null), SORT_SETTLE_MS);
    },
    [dispatch, sendRequest, uid]
  );

  const eventsList = selectedDateEvents
    .map((item) => (
      <EventItem key={item.id} {...item} onToggleImportant={toggleImportantHandler} />
    ))
    .sort(function (a, b) {
      if (a.props.timeFrom) {
        if (b.props.timeFrom)
          return (
            +a.props.timeFrom.split(":").join("") -
            +b.props.timeFrom.split(":").join("")
          );
        else return -1;
      }
      if (b.props.timeFrom) {
        if (a.props.timeFrom)
          return (
            +b.props.timeFrom.split(":").join("") -
            +a.props.timeFrom.split(":").join("")
          );
        else return 1;
      }
      if (a.props.timeTill) {
        if (b.props.timeTill)
          return (
            +a.props.timeTill.split(":").join("") -
            +b.props.timeTill.split(":").join("")
          );
        else return -1;
      }
      if (b.props.timeTill) {
        if (a.props.timeTill)
          return (
            +b.props.timeTill.split(":").join("") -
            +a.props.timeTill.split(":").join("")
          );
        else return 1;
      }
      if (a.props.important) {
        return -1;
      }
      if (b.props.important) {
        return 1;
      }
      return 0;
    });

  if (frozenOrder) {
    // Anything that arrived since the freeze has no place in it yet; it waits
    // at the end until the real sort takes over.
    const rank = new Map(frozenOrder.map((id, index) => [id, index]));
    const unranked = Number.MAX_SAFE_INTEGER;
    eventsList.sort(
      (a, b) =>
        (rank.get(a.props.id) ?? unranked) - (rank.get(b.props.id) ?? unranked)
    );
  }
  shownOrder.current = eventsList.map((item) => item.props.id);

  let whitespaceHeight = "0";
  if (windowWidth < 960) {
    whitespaceHeight = "0";
  } else {
    let freespace;
    if (window.desktop) {
      freespace = Math.max(
        0,
        windowHeight - 146 - (selectedDateEvents.length * 49 + 48)
      );
    } else {
      freespace = Math.max(
        0,
        windowHeight - 130 - (selectedDateEvents.length * 49 + 48)
      );
    }
    whitespaceHeight = freespace + "px";
  }

  return (
    <div className={wrapperClass}>
      <ul className={listClass}>
        {eventsList}
        <li className={classes.copyright}>&copy; 2015&mdash;{year} ToDoo</li>
      </ul>
      <div
        className={classes.whitespace}
        style={{ height: whitespaceHeight }}
      ></div>
    </div>
  );
};

export default Events;
